<template>
    <v-container>

        <v-row>
            <!-- AVATAR -->
            <v-col cols="12" class="text-center" @click="editAvatar">
                <v-avatar color="backGroundPrimary" size="150" class="kimsa-elevation avatar-img">
                    <v-img v-if="studentImg" :src="studentImg" eager cover/>
                    <v-icon v-else dark size="100">$account</v-icon>
                    <v-btn color="primary" fab small depressed @click="editAvatar" class="edit-avatar-btn">
                        <v-icon color="black">$edit</v-icon>
                    </v-btn>
                </v-avatar>
            </v-col>

            <!-- NAME AND COACH -->
            <v-col cols="12" class="text-center">
                <div class="student-name">
                    {{ studentName }}
                </div>
                <div class="coach-name">
                    {{ currentCoach }}
                </div>
            </v-col>

            <!-- PHYSICAL STATS -->
            <v-col cols="12">
                <div class="title-section">
                    {{ $t('student.my_last_weight') }}
                </div>
                <physical-stats :height="account.height" :weight="account.weight"/>
            </v-col>

            <!-- OBJECTIVES -->
            <v-col cols="12">
                <div class="title-section">
                    {{ $tc('objectives.objectives', 2) }}
                    <v-btn color="primary" fab small depressed @click="editObjectives" class="edit-btn">
                        <v-icon color="black">$edit</v-icon>
                    </v-btn>
                </div>
                <v-chip
                    v-for="objective in objectives"
                    :key="objective"
                    label outlined
                    color="backGroundPrimary"
                    class="black--text mr-1 mb-1"
                >
                    {{ objective }}
                </v-chip>
                <v-alert v-if="!objectives" border="left" dense outlined color="accent" icon="mdi-information-outline">
                    {{ $t('common.without_objectives') }}
                </v-alert>
            </v-col>

            <!-- INJURIES -->
            <v-col cols="12">
                <div class="title-section">
                    {{ $t('common.injuries') }}
                    <v-btn color="primary" fab x-small depressed @click="addInjury" class="add-injury">
                        <v-icon color="black">$add_circle</v-icon>
                    </v-btn>
                </div>
                <injuries-card :student-id="studentId" ref="injuries"/>
            </v-col>

            <!-- EQUIPMENTS -->
            <v-col cols="12">
                <div class="title-section">
                    {{ $t('equipments.equip') }}
                    <v-btn color="primary" fab small depressed @click="editEquipments" class="edit-btn">
                        <v-icon color="black">$edit</v-icon>
                    </v-btn>
                </div>
                <kimsa-select
                    v-if="equipments.length > 0" cols-md="4"
                    :height="64" :items="equipments"
                    readonly multiple withImages/>
                <v-alert v-else border="left" dense outlined color="accent" icon="mdi-information-outline">
                    {{ $t('common.without_equipments') }}
                </v-alert>
            </v-col>

            <!-- DELETE ACCOUNT -->
            <v-col cols="12" align="right">
                <confirm-button
                    color="error"
                    :label="$t('common.delete_account')"
                    :title="$t('common.delete_account_title')"
                    :content="$t('common.delete_account_subtitle')"
                    alert-mode
                    :loading="loadingDelete"
                    @confirm="onConfirmDelete"
                    ref="delete"
                />
            </v-col>
        </v-row>

        <upload-avatar-dialog ref="uploadPhoto"/>

    </v-container>

</template>

<script>

import {mapActions, mapState} from 'vuex'
import firebase from "@/utils/Firebase";
import UploadAvatarDialog from '@/components/student/UploadAvatarDialog'
import PhysicalStats from "@/components/student/PhysicalStats";
import KimsaSelect from '@/components/common/inputs/KimsaSelect'
import ConfirmButton from "@/components/elements/buttons/ConfirmButton";
import InjuriesCard from "@/components/student/InjuriesCard";

export default {
    components: {
        InjuriesCard,
        ConfirmButton,
        UploadAvatarDialog,
        PhysicalStats,
        KimsaSelect
    },
    data() {
        return {
            loadingDelete: false,
        }
    },
    computed: {
        ...mapState(['equipment']),
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        studentImg(vm = this) {
            return vm.currentUser?.photoURL || vm.account?.avatar || null
        },
        studentName(vm = this) {
            return vm.account?.name || '-'
        },
        coachName(vm = this) {
            return vm.account?.studentSubscription?.coachName || null
        },
        currentCoach(vm = this) {
            return vm.coachName ?
                `${vm.$tc('coach.coach', 1)}: ${vm.coachName}` :
                vm.$t('coach.no_hired_coach')
        },
        equipments(vm = this) {
            return vm.equipment.filter(equip => vm.account?.equipments?.includes(equip.name))
        },
        objectives(vm = this) {
            return vm.account?.objectives || null
        },
    },
    async mounted() {
        const vm = this
        if (vm.equipment.length === 0) await vm.bindEquipment()
    },
    methods: {
        ...mapActions(['bindEquipment']),
        editAvatar() {
            const vm = this
            vm.$refs.uploadPhoto.show()
        },
        editObjectives() {
            const vm = this
            vm.$router.push({name: 'student.profile.edit', params: {mode: 'ChooseObjectives'}})
        },
        editEquipments() {
            const vm = this
            vm.$router.push({name: 'student.profile.edit', params: {mode: 'ChooseEquipments'}})
        },
        async onConfirmDelete() {
            const vm = this
            vm.loadingDelete = true
            await vm.deleteAccount()
            vm.loadingDelete = false
            await vm.$router.replace({name: 'login'})
        },
        deleteAccount: firebase.functions().httpsCallable('httpPayments-deleteAccount'),
        addInjury() {
            const vm = this
            vm.$refs.injuries.showInjuryForm()
        }
    },
}
</script>

<style scoped>

.avatar-img {
    border: 3px solid var(--v-primary-base) !important;
    overflow: visible;
}

.student-name {
    color: #333333;
    font-size: 25px;
    line-height: 30px;
    font-weight: bold;
}

.coach-name {
    color: #666666;
    font-size: 13px;
    line-height: 18px;
}

.title-section {
    color: #707070;
    font-size: 15px;
    line-height: 20px;
    font-weight: 700;
    margin-bottom: 4px;
    position: relative;
}

.edit-btn {
    position: absolute;
    top: 0;
    right: -10px;
    border: 1px solid #333333 !important;
    z-index: 1;
}

.edit-avatar-btn {
    position: absolute;
    bottom: 0;
    right: 0;
    border: 1px solid #333333 !important;
}

.add-injury {
    border: 1px solid #333333 !important;
}

</style>